import { useCallback } from 'react';
import { useApiClient } from '../../../contexts/ApiContext';
import { toApiLoadout, toCreateGameRequest, toJoinGameRequest, toApiAction } from './rushMidAdapters';
import { toDomainSnapshot } from '../../../services/wireCast';
import type {
  Action, FighterInit, Loadout, RoundEvent, Team, RushMidMap,
  RushMidSnapshot, RushMidSnapshotPlaying, RushMidSnapshotOver,
  RushMidSpectatePlaying, RushMidSpectateOver,
  RushMidPublicFighter, RushMidScoreEntry, RushMidBuyRosterEntry, RushMidPayout,
  RushMidRoundAction,
} from '@cs-shot/battle-sim';

// Domain types come from the shared @cs-shot/battle-sim package — the SAME types
// the backend builds the snapshot from, so there are no per-field casts here.
export type Side = Team;
/** Side preference for matchmaking / party config: a hard team, or 'any' (place us on
 *  whichever team has room). The SEATED side is always a concrete Side. */
export type SidePref = Side | 'any';
export type { RushMidSnapshot };
export type Ally = RushMidBuyRosterEntry;
export type PublicFighter = RushMidPublicFighter;
export type ScoreEntry = RushMidScoreEntry;

/** Per-round payload the battle renderer/engine eats (derived from a snapshot). */
export interface RoundUpdate {
  matchId: string;
  /** battle map for this match (drives the background) */
  map: RushMidMap;
  round: number;
  events: RoundEvent[];
  /** each fighter's server-resolved action for `events`, keyed by fighter id — the true badge
   *  source. Absent on old snapshots (the renderer then derives badges from `events`). */
  actions?: Record<string, RushMidRoundAction>;
  fighters: PublicFighter[];
  /** pre-combat fighter state (full HP + loadout armor) — set ONLY on the round-0 update so a
   *  cold mount (a replay, which has no pre-round action phase) seeds the round-0 animation from
   *  the START state instead of `fighters` (which is already post-round-0 → damage would apply
   *  twice / not animate). Live seeds this itself from the action-phase frame. */
  initFighters?: PublicFighter[];
  inits?: FighterInit[];
  yourFighterId: string | null;
  yourActionSubmitted?: boolean;
  deadline: number | null;
  over: boolean;
  winner?: Team | null;
  payout?: RushMidPayout;
  scoreboard?: ScoreEntry[];
  serverSeed?: string;
  serverSeedHash?: string;
  clientSeed?: string;
}

// The ONLY wire→domain boundary lives in services/wireCast.ts (outside the no-assertion
// gate); re-exported here as the command surface's snapshot normaliser.
export const toSnapshot = toDomainSnapshot;

// Build the per-round payload the live battle / engine expect. Typed to the round
// variants (PLAYING/OVER) so every field is guaranteed — no `?.`/`?? []`.
export function snapshotToRoundUpdate(s: RushMidSnapshotPlaying | RushMidSnapshotOver): RoundUpdate {
  const base = {
    matchId: s.matchId,
    map: s.map,
    round: s.round,
    events: s.lastEvents,
    actions: s.lastActions,
    fighters: s.fighters,
    inits: s.inits,
    yourFighterId: s.yourFighterId,
  };
  if (s.status === 'OVER') {
    return {
      ...base, yourActionSubmitted: false, deadline: null, over: true,
      winner: s.winner, payout: s.payout, scoreboard: s.scoreboard,
      serverSeed: s.serverSeed, serverSeedHash: s.serverSeedHash, clientSeed: s.clientSeed,
    };
  }
  return { ...base, yourActionSubmitted: s.yourActionSubmitted, deadline: s.roundDeadline, over: false };
}

/** The spectator view → the same per-round payload, with no "you": `yourFighterId` is null,
 *  so the battle renders read-only (no haptic target, no action bar) and no personal payout. */
export function spectateToRoundUpdate(v: RushMidSpectatePlaying | RushMidSpectateOver): RoundUpdate {
  const base = {
    matchId: v.matchId,
    map: v.map,
    round: v.round,
    events: v.lastEvents,
    actions: v.lastActions,
    fighters: v.fighters,
    inits: v.inits,
    yourFighterId: null,
  };
  if (v.status === 'OVER') {
    return {
      ...base, yourActionSubmitted: false, deadline: null, over: true,
      winner: v.winner, scoreboard: v.scoreboard,
      serverSeed: v.serverSeed, serverSeedHash: v.serverSeedHash, clientSeed: v.clientSeed,
    };
  }
  return { ...base, yourActionSubmitted: false, deadline: v.roundDeadline, over: false };
}

export function snapshotAllies(s: RushMidSnapshot): Ally[] {
  return s.status === 'BUYING' ? s.allies : [];
}

/**
 * Rush Mid command surface — every call hits the typed HTTP client and returns the
 * narrowed authoritative snapshot. No Centrifugo: the client drives everything via
 * HTTP (commands re-sync inline, a poll timer pulls the snapshot while waiting).
 */
export function useRushMidMatch() {
  const apiClient = useApiClient();

  // Create an open game on a concrete side (matchMode 'bots' returns BUYING straight away).
  const createGame = useCallback(
    async (mode: number, ticket: number, side: SidePref, matchMode?: 'ranked' | 'bots') =>
      toSnapshot(await apiClient.api.rushMidControllerCreateOpenGame(toCreateGameRequest(mode, ticket, side, matchMode))),
    [apiClient],
  );
  // Sit into an existing open game on a side that has room (fills the house → BUYING).
  const joinGame = useCallback(
    async (matchId: string, side: Side) =>
      toSnapshot(await apiClient.api.rushMidControllerJoinOpenGame(matchId, toJoinGameRequest(side))),
    [apiClient],
  );
  // Leave the open game we're seated in (a party leader takes the whole party out).
  const leaveGame = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerLeaveOpenGame()),
    [apiClient],
  );
  const submitLoadout = useCallback(
    async (matchId: string, loadout: Loadout, ready: boolean) =>
      toSnapshot(await apiClient.api.rushMidControllerSubmitLoadout(matchId, { loadout: toApiLoadout(loadout), ready })),
    [apiClient],
  );
  const submitAction = useCallback(
    async (matchId: string, action: Action, round?: number) =>
      toSnapshot(await apiClient.api.rushMidControllerSubmitAction(matchId, toApiAction(action, round))),
    [apiClient],
  );
  const leaveMatch = useCallback(
    async (matchId: string) => toSnapshot(await apiClient.api.rushMidControllerLeaveMatch(matchId)),
    [apiClient],
  );
  const getSnapshot = useCallback(
    async () => toSnapshot(await apiClient.api.rushMidControllerSnapshot()),
    [apiClient],
  );

  return { createGame, joinGame, leaveGame, submitLoadout, submitAction, leaveMatch, getSnapshot };
}
